import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import '../styles/Admin.css';
import API_BASE from '../config';

// ─── Role + Department helpers ───────────────────────────────────────────────
const getDept = (u) => {
  const d = u?.department;
  return (typeof d === 'string' ? d : d?.name || '').toUpperCase().trim();
};

const isSuperAdmin    = (u) => u?.role === 'SUPERADMIN';
const isCountryAdmin  = (u) => u?.role === 'COUNTRY_ADMIN';
const isFullAdmin     = (u) => isSuperAdmin(u) || isCountryAdmin(u);

const isITAdmin       = (u) => u?.role === 'IT' || (u?.role === 'ADMIN' && (getDept(u) === 'INFORMATION TECHNOLOGY' || getDept(u) === 'IT' || getDept(u) === 'LOCAL IT SUPPORT'));
const isFacilityAdmin = (u) => u?.role === 'FACILITY' || (u?.role === 'ADMIN' && getDept(u) === 'FACILITY');
const isRecruitAdmin  = (u) => u?.role === 'ADMIN' && getDept(u) === 'RECRUITMENT';
const isHRAdmin       = (u) => u?.role === 'HR' || (u?.role === 'ADMIN' && (getDept(u) === 'HR' || getDept(u) === 'HUMAN RESOURCES'));

const canSeeIT        = (u) => isFullAdmin(u) || isITAdmin(u);
const canSeeFacility  = (u) => isFullAdmin(u) || isFacilityAdmin(u) || isITAdmin(u);
const canSeeRecruit   = (u) => isFullAdmin(u) || isRecruitAdmin(u);
const canSeeHR        = (u) => isFullAdmin(u) || isHRAdmin(u);
const canSeeDocs      = (u) => isFullAdmin(u) || isITAdmin(u) || isRecruitAdmin(u) || isHRAdmin(u);

function AdminDashboard() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeAccess: 0,
    pendingReviews: 0,
    criticalAuditEvents: 0
  });
  const [loading, setLoading] = useState(true);
  const [recentAssigned, setRecentAssigned] = useState([]);
  const [recentReturned, setRecentReturned] = useState([]);
  const [facilityRows, setFacilityRows] = useState([]);
  const [onboardingRows, setOnboardingRows] = useState([]);

  useEffect(() => {
    // Pure facility admin → redirect to facility board directly
    if (isFacilityAdmin(user) && !isFullAdmin(user) && !isITAdmin(user)) {
      navigate('/admin/facility', { replace: true }); return;
    }
    if (isFullAdmin(user)) fetchStats();
    else setLoading(false);
    if (canSeeIT(user)) fetchRecentEquipment();
    if (canSeeFacility(user)) fetchFacilityBoard();
    if (canSeeHR(user) && !isRecruitAdmin(user)) fetchOnboarding();
  }, [user]); // eslint-disable-line

  const fetchRecentEquipment = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/admin/equipment-recent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRecentAssigned(data.recentAssigned || []);
        setRecentReturned(data.recentReturned || []);
      }
    } catch (err) {
      console.error('Error fetching recent equipment:', err);
    }
  };

  const fetchFacilityBoard = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/admin/equipment-assignments?skip=0&take=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const deliveries = (data.assignments || []).filter(r => r.receiveMethod === 'delivery');
        setFacilityRows(deliveries);
      }
    } catch (err) {
      console.error('Error fetching facility board:', err);
    }
  };

  const fetchOnboarding = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/recruitment/candidates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOnboardingRows((data.candidates || []).slice(0, 20));
      }
    } catch (err) {
      console.error('Error fetching onboarding:', err);
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—';

  const assignStatus = (r) => {
    const keys = ['computer','monitor1','monitor2','token','other'];
    if (r.returned) return <span className="eq-badge eq-badge-returned">Returned</span>;
    const anyPartial = keys.some(k => r[k + 'Returned']);
    if (anyPartial) return <span className="eq-badge eq-badge-partial">Partial</span>;
    return <span className="eq-badge eq-badge-active">Active</span>;
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(prev => ({
          ...prev,
          totalUsers: data.totalCount
        }));
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const badge = (color, text) => {
    const colors = { green: ['#e8f5e9','#2e7d32'], orange: ['#fff3e0','#e65100'], grey: ['#f3f4f6','#555'], blue: ['#e3f2fd','#1565c0'] };
    const [bg, fg] = colors[color] || colors.grey;
    return <span style={{ display:'inline-block', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:700, background:bg, color:fg }}>{text}</span>;
  };

  if (loading) {
    return <div className="admin-container"><p>Loading dashboard...</p></div>;
  }

  // Determine heading subtitle
  let headingSub = 'Full Administrative Access';
  if (!isFullAdmin(user)) {
    if (isITAdmin(user))       headingSub = 'IT Administration';
    else if (isHRAdmin(user))  headingSub = 'HR Administration';
    else if (isRecruitAdmin(user)) headingSub = 'Recruitment';
    else if (isFacilityAdmin(user)) headingSub = 'Facility Administration';
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Administrative Dashboard</h1>
      </div>

      {/* Statistics Cards – SUPERADMIN / COUNTRY_ADMIN only */}
      {isFullAdmin(user) && (
        <div className="stats-grid">
          <div className="stat-card"><h3>{stats.totalUsers}</h3><p>Active Users</p></div>
          <div className="stat-card"><h3>{stats.activeAccess}</h3><p>Active Access Controls</p></div>
          <div className="stat-card"><h3>{stats.pendingReviews}</h3><p>Pending Access Reviews</p></div>
          <div className="stat-card critical"><h3>{stats.criticalAuditEvents}</h3><p>Critical Audit Events</p></div>
        </div>
      )}

      {/* Admin Modules Grid */}
      <div className="admin-modules">
        <h2>Management Modules — {headingSub}</h2>

        <div className="modules-grid">

          {/* ── SUPERADMIN / COUNTRY_ADMIN only ─────────────────── */}
          {isFullAdmin(user) && (
            <Link to="/admin/users" className="module-card">
              <div className="module-icon">👥</div>
              <h3>User Management</h3>
              <p>Manage users, roles and system access. Assign departments and privileges.</p>
              <span className="iso-badge">RBAC</span>
            </Link>
          )}
          {isFullAdmin(user) && (
            <Link to="/admin/roles" className="module-card">
              <div className="module-icon">🔐</div>
              <h3>Roles & Permissions</h3>
              <p>Define roles and assign granular permissions based on principle of least access.</p>
              <span className="iso-badge">PoLA</span>
            </Link>
          )}
          {isFullAdmin(user) && (
            <Link to="/admin/audit-logs" className="module-card">
              <div className="module-icon">📊</div>
              <h3>Audit Logs</h3>
              <p>Track all administrative actions. Generate compliance reports and audit trails.</p>
              <span className="iso-badge">Accountability</span>
            </Link>
          )}
          {isFullAdmin(user) && (
            <Link to="/admin/countries" className="module-card">
              <div className="module-icon">🌍</div>
              <h3>Country Management</h3>
              <p>Manage countries and associated sites or offices.</p>
              <span className="iso-badge">Global</span>
            </Link>
          )}
          {isFullAdmin(user) && (
            <Link to="/admin/clients" className="module-card">
              <div className="module-icon">🏢</div>
              <h3>Client Management</h3>
              <p>Manage clients and campaign sites per country.</p>
              <span className="iso-badge">Global</span>
            </Link>
          )}

          {/* ── Master Directory: Full Admin + HR ────────────────── */}
          {(isFullAdmin(user) || isHRAdmin(user)) && (
            <Link to="/admin/master-directory" className="module-card">
              <div className="module-icon">📒</div>
              <h3>Master Directory</h3>
              <p>Full employee directory with all records and departments.</p>
              <span className="iso-badge">Directory</span>
            </Link>
          )}

          {/* ── IT tiles ─────────────────────────────────────────── */}
          {canSeeIT(user) && (
            <Link to="/admin/assets" className="module-card">
              <div className="module-icon">📦</div>
              <h3>Assets Management</h3>
              <p>Define and manage information assets. Set classification and ownership.</p>
              <span className="iso-badge">Asset Owner</span>
            </Link>
          )}
          {canSeeIT(user) && (
            <Link to="/admin/equipment-assignment" className="module-card">
              <div className="module-icon">💻</div>
              <h3>Assign Equipment</h3>
              <p>Assign new or replacement IT equipment to employees. Generate assignment documents.</p>
              <span className="iso-badge">IT</span>
            </Link>
          )}
          {canSeeIT(user) && (
            <Link to="/admin/equipment-return" className="module-card">
              <div className="module-icon">🔄</div>
              <h3>Equipment Return</h3>
              <p>Process asset returns for departing employees. Generate return protocol PDF.</p>
              <span className="iso-badge">IT</span>
            </Link>
          )}
          {canSeeIT(user) && (
            <Link to="/admin/equipment-log" className="module-card">
              <div className="module-icon">📋</div>
              <h3>Equipment Log</h3>
              <p>View all assigned equipment. Track delivery status and sign-off protocols.</p>
              <span className="iso-badge">IT</span>
            </Link>
          )}

          {/* ── Document Browser: IT + HR + Recruitment ──────────── */}
          {canSeeDocs(user) && (
            <Link to="/admin/documents" className="module-card">
              <div className="module-icon">📁</div>
              <h3>Document Browser</h3>
              <p>Browse all employee folders, view handover and return protocols by employee.</p>
              <span className="iso-badge">Docs</span>
            </Link>
          )}

          {/* ── Facility Board: IT + Full Admin ──────────────────── */}
          {canSeeFacility(user) && (
            <Link to="/admin/facility" className="module-card">
              <div className="module-icon">🚚</div>
              <h3>Facility Board</h3>
              <p>Delivery coordination — current delivery status per assignment.</p>
              <span className="iso-badge">Facility</span>
            </Link>
          )}

          {/* ── RECRUITMENT ──────────────────────────────────────── */}
          {canSeeRecruit(user) && (
            <Link to="/admin/recruitment/onboarding" className="module-card">
              <div className="module-icon">🎉</div>
              <h3>New Hires – Pipeline</h3>
              <p>Track recruitment candidates, manage status changes and document signing for hired employees.</p>
              <span className="iso-badge">Recruitment</span>
            </Link>
          )}

          {/* ── HR ───────────────────────────────────────────────── */}
          {canSeeHR(user) && (
            <Link to="/admin/lifecycle" className="module-card">
              <div className="module-icon">🚀</div>
              <h3>User Lifecycle</h3>
              <p>Manage onboarding and offboarding workflows. Ensure proper access provisioning.</p>
              <span className="iso-badge">HR</span>
            </Link>
          )}

        </div>
      </div>

      {/* ── Recent Equipment Tables – IT dashboard ─────────────────────── */}
      {canSeeIT(user) && (
        <div className="eq-recent-section">
          <div className="eq-recent-card">
            <div className="eq-recent-header">
              <span className="eq-recent-icon">💻</span>
              <h3>Last 15 Assigned Equipment</h3>
            </div>
            {recentAssigned.length === 0 ? (
              <p className="eq-recent-empty">No assignments yet.</p>
            ) : (
              <table className="eq-mini-table">
                <thead>
                  <tr><th>#</th><th>Employee</th><th>Type</th><th>Assigned</th><th>By</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {recentAssigned.map((r, i) => (
                    <tr key={r.id}>
                      <td>{i + 1}</td>
                      <td><span className="eq-emp-name">{r.employeeName}</span><span className="eq-emp-id">{r.employeeId}</span></td>
                      <td>{r.equipmentType || '—'}</td>
                      <td>{fmtDate(r.createdAt)}</td>
                      <td>{r.createdBy ? `${r.createdBy.firstName} ${r.createdBy.lastName}` : '—'}</td>
                      <td>{assignStatus(r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="eq-recent-card">
            <div className="eq-recent-header">
              <span className="eq-recent-icon">🔄</span>
              <h3>Last 15 Returned Equipment</h3>
            </div>
            {recentReturned.length === 0 ? (
              <p className="eq-recent-empty">No returns yet.</p>
            ) : (
              <table className="eq-mini-table">
                <thead>
                  <tr><th>#</th><th>Employee</th><th>Type</th><th>Returned</th><th>Processed By</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  {recentReturned.map((r, i) => (
                    <tr key={r.id}>
                      <td>{i + 1}</td>
                      <td><span className="eq-emp-name">{r.employeeName}</span><span className="eq-emp-id">{r.employeeId}</span></td>
                      <td>{r.equipmentType || '—'}</td>
                      <td>{fmtDate(r.returnedAt)}</td>
                      <td>{r.returnedBy ? `${r.returnedBy.firstName} ${r.returnedBy.lastName}` : '—'}</td>
                      <td className="eq-notes-cell">{r.returnNotes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── Facility Board inline table – IT / Full Admin ──────────────── */}
      {canSeeFacility(user) && facilityRows.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 18, marginBottom: 12 }}>🚚 Facility Board — Delivery Overview</h2>
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Employee</th><th>ID</th><th>Department</th><th>Address</th><th>Status</th><th>Date</th>
                </tr>
              </thead>
              <tbody>
                {facilityRows.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.employeeName}</td>
                    <td style={{ fontFamily: 'monospace' }}>{r.employeeId}</td>
                    <td>{r.departmentName || r.department?.name || '—'}</td>
                    <td>{r.address || '—'}</td>
                    <td>
                      {r.returned ? badge('grey', 'Returned') :
                       r.deliveryConfirmed ? badge('green', '✔ Processed') :
                       badge('orange', '⏳ Waiting')}
                    </td>
                    <td>{fmtDate(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Onboarding read-only table for HR Admin ────────────────────── */}
      {canSeeHR(user) && onboardingRows.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 18, marginBottom: 12 }}>🎉 New Hires – Pipeline (Recent)</h2>
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th><th>Email</th><th>Position</th><th>Campaign</th><th>Status</th><th>Start Date</th>
                </tr>
              </thead>
              <tbody>
                {onboardingRows.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.firstName} {r.lastName}</td>
                    <td>{r.email || '—'}</td>
                    <td>{r.position || '—'}</td>
                    <td>{r.campaign || '—'}</td>
                    <td>{badge(
                      r.status === 'HIRED' ? 'green' : r.status === 'IN_PROCESS' ? 'orange' : r.status === 'REJECTED' ? 'grey' : 'blue',
                      r.status || '—'
                    )}</td>
                    <td>{fmtDate(r.startDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}

export default AdminDashboard;
